import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Trash2, MoreHorizontal, Search, Bell, BellOff, X, Files, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useChatPresence } from '../../../context/ChatPresenceContext';
import { setChatChannelMute } from '../../../utils/api';
import ChatAvatar from '../parts/ChatAvatar';
import { useChatMedia, REVEAL_MESSAGE_EVENT } from '../parts/ChatMediaContext';
import { channelDisplayName, collectViewerItems, firstName, formatStamp } from '../utils/chatFormat';
import styles from '../Chat.module.css';

const lastSeenText = (timestamp) => {
  if (!timestamp) return 'Away';
  const diffMins = Math.floor((Date.now() - new Date(timestamp).getTime()) / 60000);
  if (diffMins < 1) return 'Last seen just now';
  if (diffMins < 60) return `Last seen ${diffMins}m ago`;
  if (diffMins < 1440) return `Last seen ${Math.floor(diffMins / 60)}h ago`;
  if (diffMins < 2880) return 'Last seen yesterday';
  return `Last seen ${new Date(timestamp).toLocaleDateString()}`;
};

const CustomChannelHeader = ({ onBack, isMobile, channel, onDeleteChannel }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [, setTick] = useState(0);
  const menuRef = useRef(null);
  const currentUser = window.streamChatClient?.user;
  const { getUserOnlineStatus } = useChatPresence();
  const { openMedia } = useChatMedia();

  const members = Object.values(channel?.state?.members || {});
  const others = members.filter((m) => m.user?.id !== currentUser?.id);
  const isGroup = members.length > 2;
  const otherUser = others.length === 1 ? others[0].user : null;
  const channelName = channelDisplayName(channel, currentUser?.id);
  const isOwner = channel?.data?.created_by?.id === currentUser?.id;
  const mediaItems = collectViewerItems(channel?.state?.messages || []);

  // Repaint the file count and presence as the conversation changes.
  useEffect(() => {
    if (!channel) return undefined;
    const repaint = () => setTick((t) => t + 1);
    const events = ['message.new', 'message.updated', 'message.deleted', 'user.presence.changed', 'member.added', 'member.removed'];
    events.forEach((e) => channel.on(e, repaint));
    return () => events.forEach((e) => channel.off(e, repaint));
  }, [channel]);

  useEffect(() => {
    if (!channel) return undefined;
    const syncMuteStatus = () => setIsMuted(channel.muteStatus?.().muted || false);
    syncMuteStatus();
    const client = channel.getClient?.();
    client?.on?.('notification.channel_mutes_updated', syncMuteStatus);
    return () => client?.off?.('notification.channel_mutes_updated', syncMuteStatus);
  }, [channel]);

  useEffect(() => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults(null);
  }, [channel]);

  useEffect(() => {
    if (!showMenu) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) { setShowMenu(false); setConfirmDelete(false); } };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showMenu]);

  // Debounced in-conversation search.
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q || !channel) { setSearchResults(null); setIsSearching(false); return undefined; }
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = await channel.search(q, { limit: 20 });
        setSearchResults(response.results || []);
      } catch (error) {
        console.error('[CustomChannelHeader] Error searching messages:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, channel]);

  const handleDeleteChannel = async () => {
    if (!channel || isDeleting) return;
    setIsDeleting(true);
    try {
      await channel.delete();
      setShowMenu(false);
      onDeleteChannel?.();
    } catch (error) {
      console.error('[CustomChannelHeader] Error deleting channel:', error);
      toast.error('Could not delete this conversation.');
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleToggleMute = async () => {
    if (!channel) return;
    const nextMuted = !isMuted;
    const syncClientMutedChannels = (response) => {
      const client = channel.getClient();
      if (response?.channel_mutes) { client.mutedChannels = response.channel_mutes; return; }
      if (response?.own_user?.channel_mutes) { client.mutedChannels = response.own_user.channel_mutes; return; }
      const existing = Array.isArray(client.mutedChannels) ? client.mutedChannels : [];
      if (nextMuted && response?.channel_mute) {
        client.mutedChannels = [...existing.filter((mute) => mute.channel?.cid !== channel.cid), response.channel_mute];
      } else if (!nextMuted) {
        client.mutedChannels = existing.filter((mute) => mute.channel?.cid !== channel.cid);
      }
    };

    try {
      const response = nextMuted ? await channel.mute() : await channel.unmute();
      syncClientMutedChannels(response);
      setIsMuted(nextMuted);
      setChatChannelMute({ cid: channel.cid, channelType: channel.type, channelId: channel.id, muted: nextMuted })
        .catch((error) => console.error('[CustomChannelHeader] Error saving mute preference:', error));
    } catch (error) {
      console.error('[CustomChannelHeader] Error toggling mute:', error);
      toast.error('Could not change notifications for this conversation.');
    }
  };

  const copyConversationLink = async () => {
    const url = `${window.location.origin}/chat?cid=${encodeURIComponent(channel.cid)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch (_) {
      toast.error('Could not copy the link.');
    }
    setShowMenu(false);
  };

  const revealResult = (message) => {
    window.dispatchEvent(new CustomEvent(REVEAL_MESSAGE_EVENT, { detail: { messageId: message.id } }));
  };

  let meta = null;
  if (isGroup) {
    const active = others.filter((m) => getUserOnlineStatus(m.user?.id).online).map((m) => firstName(m.user));
    meta = (
      <>
        <span>{members.length} members</span>
        {active.length > 0 && (
          <>
            <span aria-hidden="true">·</span>
            <span className={styles.presenceDot} />
            <span>
              {active.length <= 2 ? `${active.join(' and ')} active` : `${active.length} active`}
            </span>
          </>
        )}
      </>
    );
  } else if (otherUser) {
    const { online, lastSeen } = getUserOnlineStatus(otherUser.id);
    meta = online
      ? <><span className={styles.presenceDot} /><span>Active now</span></>
      : <span>{lastSeenText(lastSeen || otherUser.last_active)}</span>;
  }

  return (
    <>
      <div className={styles.chatHeader}>
        {isMobile && (
          <button type="button" onClick={onBack} className={styles.iconBtn} aria-label="Back to conversations">
            <ArrowLeft size={18} />
          </button>
        )}
        <ChatAvatar
          name={channelName}
          id={isGroup ? channel?.id : otherUser?.id}
          image={!isGroup ? otherUser?.image : null}
          group={isGroup}
        />
        <div className={styles.headerInfo}>
          <h2 className={styles.headerName} title={channelName}>
            {channelName}
            {isMuted && <BellOff size={13} style={{ color: 'var(--lodge-onyx-500)', flex: '0 0 auto' }} aria-label="Muted" />}
          </h2>
          {meta && <div className={styles.headerMeta}>{meta}</div>}
        </div>

        <div className={styles.headerActions}>
          {!isMobile && (
            <button
              type="button"
              className={styles.headerTextBtn}
              onClick={() => openMedia(mediaItems, mediaItems.length - 1, { conversation: isGroup ? channelName : null })}
              disabled={mediaItems.length === 0}
              title={mediaItems.length ? 'Browse files shared here' : 'No files shared yet'}
            >
              <Files size={15} aria-hidden="true" />
              Files {mediaItems.length > 0 && <span>{mediaItems.length}</span>}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowSearch((v) => !v)}
            className={`${styles.iconBtn} ${showSearch ? styles.active : ''}`}
            aria-label="Search this conversation"
            aria-pressed={showSearch}
          >
            <Search size={16} />
          </button>
          <button
            type="button"
            onClick={handleToggleMute}
            className={styles.iconBtn}
            aria-label={isMuted ? 'Turn notifications on' : 'Mute notifications'}
            title={isMuted ? 'Notifications off — click to turn on' : 'Mute notifications'}
          >
            {isMuted ? <BellOff size={16} /> : <Bell size={16} />}
          </button>
          <div className={styles.menuAnchor} ref={menuRef}>
            <button
              type="button"
              onClick={() => { setShowMenu((v) => !v); setConfirmDelete(false); }}
              className={`${styles.iconBtn} ${showMenu ? styles.active : ''}`}
              aria-label="Conversation options"
              aria-haspopup="menu"
              aria-expanded={showMenu}
            >
              <MoreHorizontal size={16} />
            </button>
            {showMenu && (
              <div className={styles.dropdown} role="menu">
                {confirmDelete ? (
                  <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10, width: 260 }}>
                    <span className={styles.confirmTitle}>Delete this conversation?</span>
                    <span className={styles.confirmBody}>It’s removed for everyone in it, with all its messages and files.</span>
                    <div className={styles.confirmActions}>
                      <button type="button" className={`${styles.btnOutline} ${styles.btnSm}`} onClick={() => setConfirmDelete(false)}>Cancel</button>
                      <button type="button" className={`${styles.btnDanger} ${styles.btnSm}`} onClick={handleDeleteChannel} disabled={isDeleting}>
                        {isDeleting ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {isMobile && (
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.dropdownItem}
                        disabled={!mediaItems.length}
                        onClick={() => { setShowMenu(false); openMedia(mediaItems, mediaItems.length - 1, { conversation: isGroup ? channelName : null }); }}
                      >
                        <Files size={15} />
                        <span className={styles.dropdownItemLabel}>Files</span>
                        <span className={styles.kbd}>{mediaItems.length}</span>
                      </button>
                    )}
                    <button type="button" role="menuitem" className={styles.dropdownItem} onClick={copyConversationLink}>
                      <Link2 size={15} />
                      <span className={styles.dropdownItemLabel}>Copy link to conversation</span>
                    </button>
                    <button type="button" role="menuitem" className={styles.dropdownItem} onClick={() => { handleToggleMute(); setShowMenu(false); }}>
                      {isMuted ? <Bell size={15} /> : <BellOff size={15} />}
                      <span className={styles.dropdownItemLabel}>{isMuted ? 'Turn notifications on' : 'Mute notifications'}</span>
                    </button>
                    {isOwner && (
                      <>
                        <div className={styles.dropdownDivider} />
                        <button type="button" role="menuitem" className={`${styles.dropdownItem} ${styles.danger}`} onClick={() => setConfirmDelete(true)}>
                          <Trash2 size={15} />
                          <span className={styles.dropdownItemLabel}>Delete conversation</span>
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showSearch && (
        <div className={styles.threadSearch}>
          <div className={styles.threadSearchRow}>
            <label className={styles.searchWrap}>
              <span className={styles.srOnly}>Search messages in {channelName}</span>
              <Search size={15} className={styles.searchIcon} aria-hidden="true" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setShowSearch(false); setSearchQuery(''); } }}
                placeholder={`Search ${channelName}`}
                className={styles.searchInput}
                autoFocus
              />
              {isSearching && <span className={styles.spinner} style={{ position: 'absolute', right: 10, top: 9, width: 16, height: 16 }} />}
            </label>
            <button type="button" className={styles.iconBtn} onClick={() => { setShowSearch(false); setSearchQuery(''); }} aria-label="Close search">
              <X size={16} />
            </button>
          </div>
          {searchResults && (
            <div className={styles.searchResults}>
              {searchResults.length === 0 && !isSearching && (
                <div className={styles.listNote} style={{ padding: '4px 8px' }}>No messages match “{searchQuery.trim()}”.</div>
              )}
              {searchResults.map(({ message }) => (
                <button key={message.id} type="button" className={styles.searchResult} onClick={() => revealResult(message)}>
                  <ChatAvatar name={message.user?.name || message.user?.id} id={message.user?.id} image={message.user?.image} size="sm" />
                  <span className={styles.searchResultText}>
                    <span className={styles.searchResultMeta}>
                      <strong style={{ color: 'var(--lodge-onyx-900)', fontWeight: 600 }}>{message.user?.name || message.user?.id}</strong>
                      {' · '}{formatStamp(message.created_at)}
                    </span>
                    <span className={styles.searchResultBody}>{message.text || 'Attachment'}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CustomChannelHeader;
